import {
  differenceInDays,
  Duration,
  format,
  formatDuration,
  intervalToDuration,
  isSameDay,
  parseISO,
  subMilliseconds,
} from "date-fns";
import zip from "lodash/zip";
import moment from "moment-timezone";
import pluralize from "pluralize";
import {TZDate} from "react-day-picker";

import {AccountUser} from "@/lib/types/accounts.ts";
import {
  DateFormat,
  DateFormatName,
  DatePickerFormat,
  TimeFormat,
  TimeFormatName,
  TimePickerFormat,
} from "@/lib/types/common.ts";

export const shortDateTimeUser = (date: string, user: AccountUser) => {
  return shortDateTime(date, user.current_date_format, user.current_time_format, user.account.timezone);
};

export const shortDateUser = (date: string, user: AccountUser) => {
  return dateOnly(date, user.current_date_format, user.account.timezone);
};

export const shortDateTime = (
  date: string,
  dateFormat: DateFormatName,
  timeFormat: TimeFormatName,
  timezone: string
): string => {
  return moment.utc(date).tz(timezone).format(`${DateFormat[dateFormat]} ${TimeFormat[timeFormat]}`);
};

export const dateOnly = (date: string, dateFormat: DateFormatName, timezone: string): string => {
  return moment.utc(date).tz(timezone).format(DateFormat[dateFormat]);
};

export const timeOnly = (date: string, timeFormat: TimeFormatName, timezone: string) => {
  return moment.utc(date).tz(timezone).format(TimeFormat[timeFormat]);
};

// Pill periods are half-open (a day ends at the next midnight); show the last instant inside it.
// 1ms rather than 1s because the final pill is clamped to an inclusive 23:59:59.999 window end.
export const inclusivePeriodEnd = (periodEnd: Date): Date => subMilliseconds(periodEnd, 1);

// Calendar days are the account's or page's, not the viewer's browser zone.
export const isSameDayInZone = (a: Date, b: Date, timezone: string): boolean =>
  isSameDay(new TZDate(a, timezone), new TZDate(b, timezone));

export const timezoneAbbr = (timezone: string, date: Date | number = new Date()) => {
  return moment.tz(date, timezone).format("z");
};

export const formatDateTime = (
  date: Date,
  timezone: string,
  currentDateFormat?: DateFormatName,
  currentTimeFormat?: TimeFormatName
) => {
  // the date-fns format function doesn't have a way to get timezone abbreviation
  const tzAbbr = timezoneAbbr(timezone, date);
  const dateToFormat = new TZDate(date, timezone);

  return (
    format(
      dateToFormat,
      `${DatePickerFormat[currentDateFormat ?? DateFormatName.DEFAULT]} ${TimePickerFormat[currentTimeFormat ?? TimeFormatName.DEFAULT]}`
    ) + ` ${tzAbbr}`
  );
};

export const shortFormatDateRange = (
  start: Date,
  end: Date,
  timezone: string,
  currentDateFormat?: DateFormatName,
  currentTimeFormat?: TimeFormatName
) => {
  // the date-fns format function doesn't have a way to get timezone abbreviation
  const tzAbbr = timezoneAbbr(timezone, end);
  return (
    format(
      new TZDate(start, timezone),
      `${DatePickerFormat[currentDateFormat ?? DateFormatName.DEFAULT]} ${TimePickerFormat[currentTimeFormat ?? TimeFormatName.DEFAULT]}`
    ) +
    ` - ` +
    format(new TZDate(end, timezone), `${TimePickerFormat[currentTimeFormat ?? TimeFormatName.DEFAULT]}`) +
    ` ${tzAbbr}`
  );
};

/**
 * Rounds a seconds duration to its 2 largest components
 * of days, hours, mins, secs.
 */
export const roundDurationSecs = (seconds: number): number => {
  if (seconds >= 24 * 60 * 60) {
    return Math.round(seconds / 60 / 60) * 60 * 60;
  } else if (seconds >= 60 * 60) {
    return Math.round(seconds / 60) * 60;
  } else {
    return Math.round(seconds);
  }
};

export const getInitials = (...input: string[]): string => {
  return input.map((i) => i.charAt(0).toUpperCase()).join("");
};

interface ShortDurationOptions {
  roundSeconds: boolean;
}

/**
 * Formats a duration in seconds into it's largest 2 components.
 * @deprecated: please use shortDurationFn if you don't need rounding
 * shortDuration works differently from shortDuration written in Python
 */
export const shortDuration = (secs: number, options?: ShortDurationOptions): string => {
  let r = roundDurationSecs(secs);

  const s = r % 60;
  r = Math.floor(r / 60);
  const m = r % 60;
  r = Math.floor(r / 60);
  const h = r % 24;
  r = Math.floor(r / 24);
  const d = r;

  let components = zip([d, h, m, s], ["d", "h", "m", "s"]);

  while (components.length > 1 && components[0][0] == 0) {
    components.shift();
  }

  if (options && options.roundSeconds) {
    // if duration is less than a minute - return `< 1m`, round to whole minutes otherwise
    if (components[components.length - 1][1] === "s") {
      if (components.length == 1) {
        if (components[0][0] === 0) {
          return "0s";
        } else {
          return "< 1m";
        }
      } else {
        // @ts-expect-error TS2532
        const shouldAddMinute = Math.round(components[components.length - 1][0] / 60) === 1;
        if (shouldAddMinute) {
          // @ts-expect-error TS2532
          components[components.length - 2][0] = components[components.length - 2][0] + 1;
        }
        components = components.slice(0, components.length - 1);
      }
    }
  }

  components = components.slice(0, 2);
  // @ts-expect-error TS2345
  return components.map((c: [number, string]) => c[0].toString() + c[1]).join(" ");
};

export const seconds = (v: number, precision = 2) => {
  return `${v.toFixed(precision)}s`;
};

/**
 * Milliseconds from seconds.
 */
export const milliseconds = (seconds: number) => {
  return `${Math.round(seconds * 1000.0)}ms`;
};

export const autoDuration = (v: number) => {
  return v > 1.0 ? seconds(v) : milliseconds(v);
};

export const humanFriendlyInt = (value: number): string => {
  return Intl.NumberFormat("en", {notation: "compact"}).format(value);
};

export const thousandSeparatedInt = (value: number): string => {
  return value < 1000
    ? value.toString()
    : Intl.NumberFormat("en", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
};

export const shortCurrency = (val: number, maxFracDigits = 0) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: maxFracDigits,
  }).format(val);
};

export const percentage = (v: number, maxFracDigits = 4): string => {
  const percent = v * 100.0;
  return v % 1 === 0 ? `${Math.trunc(percent)}%` : `${percent.toFixed(maxFracDigits)}%`;
};

/**
 * Formats an uptime fraction (0..1) as a percentage that never rounds up to 100% when there is any
 * real downtime — a page showing outages must not read "100%". Exactly 1 (zero downtime) shows
 * "100%"; anything below rounds normally but is capped just under 100 (e.g. "99.999%"). CPL-1057.
 */
export const cappedUptimePercentage = (fraction: number | null | undefined, fractionDigits = 3): string => {
  if (fraction == null) return "n/a";
  if (fraction >= 1) return "100%";
  const rounded = Number((fraction * 100).toFixed(fractionDigits));
  const capped = rounded >= 100 ? 100 - 1 / 10 ** fractionDigits : rounded;
  return `${capped}%`;
};

export const to2DigitsTime = (date?: Date | null): string => {
  if (!date) {
    return "00:00";
  }

  return date.toLocaleTimeString([], {hour: "2-digit", hour12: false, minute: "2-digit"});
};

const formatShortDurationLocale: Record<string, string> = {
  xSeconds: "{{count}}s",
  xMinutes: "{{count}}m",
  xHours: "{{count}}h",
  xDays: "{{count}}d",
  xYears: "{{count}}y",
};

const shortEnLocale = {
  formatDistance: (token: string, count: string) => formatShortDurationLocale[token].replace("{{count}}", count),
};

const formatLongDurationLocale: Record<string, string> = {
  xSeconds: "{{count}} seconds",
  xMinutes: "{{count}} minutes",
  xHours: "{{count}} hours",
  xDays: "{{count}} days",
  xYears: "{{count}} years",
};

const longEnLocale = {
  // Every long-form unit above is plural, so a count of one drops the trailing "s": "1 hour", not "1 hours".
  formatDistance: (token: string, count: string) => {
    const label = formatLongDurationLocale[token].replace("{{count}}", count);
    return Number(count) === 1 ? label.replace(/s$/, "") : label;
  },
};

export enum DurationFormatOptions {
  SHORT = "SHORT",
  SHORT_TOP2 = "SHORT_TOP2",
  LONG = "LONG",
}

const _DurationFormatOptionMapping = {
  [DurationFormatOptions.SHORT]: shortEnLocale,
  [DurationFormatOptions.SHORT_TOP2]: shortEnLocale,
  [DurationFormatOptions.LONG]: longEnLocale,
};

/**
 * returns duration in format of 4d 3h 2m or 3h 2m 1s with DurationFormatOptions.SHORT
 * returns only the two largest components (e.g. 4d 3h) with DurationFormatOptions.SHORT_TOP2
 * and 4 day 3 hours, etc with DurationFormatOptions.LONG
 * **/
const _durationFn = (
  duration: Duration,
  days: number,
  years: number,
  locale: DurationFormatOptions = DurationFormatOptions.SHORT
): string => {
  const input = {
    days: days,
    hours: duration.hours,
    minutes: duration.minutes,
    years: years,
    seconds: 0,
  };
  if (days < 1) {
    input.seconds = duration.seconds || 0;
  }
  // @ts-expect-error T2322 ignore locale options argument
  const result = formatDuration(input, {locale: _DurationFormatOptionMapping[locale]});
  if (locale === DurationFormatOptions.SHORT_TOP2) {
    const parts = result.split(" ").filter(Boolean);
    return parts.slice(0, 2).join(" ") || "0s";
  }
  return result;
};

export const shortDurationFromInterval = (start: number, end: number): string => {
  const duration = intervalToDuration({start: start, end: end});
  const days = differenceInDays(end, start);
  return _durationFn(duration, days, 0);
};

export const durationFn = (
  sinceISO: string,
  toDate?: string,
  maxYears?: number,
  locale: DurationFormatOptions = DurationFormatOptions.SHORT
): string => {
  const since = parseISO(sinceISO);
  const upTo = toDate ? parseISO(toDate) : new Date();
  const duration = intervalToDuration({
    start: since,
    end: upTo,
  });
  let days = Math.floor(differenceInDays(upTo, since));
  const years = Math.floor(days / 365);
  if (maxYears !== undefined && years >= maxYears) {
    return `${maxYears}+ years`;
  }
  days = days - years * 365;
  return _durationFn(duration, days, years, locale);
};

export const durationSecFn = (sec: number, locale: DurationFormatOptions = DurationFormatOptions.SHORT): string => {
  const duration = intervalToDuration({start: 0, end: sec * 1000});
  const minutes = sec / 60 - (duration.minutes || 0);
  const hours = minutes / 60 - (duration.hours || 0);
  let days = Math.floor(hours / 24);
  const years = Math.floor(days / 365);
  days = days - years * 365;
  return _durationFn(duration, days, years, locale);
};

export const renderFormattedQuantity = (quantity: number, maxFracDigits = 0) => {
  if (quantity > 1000000) {
    return `${(quantity / 1000000).toFixed(maxFracDigits)}M`;
  } else if (quantity > 1000) {
    return `${(quantity / 1000).toFixed(0)}K`;
  } else {
    return quantity;
  }
};

/**
 * Replaces newlines with line breaks.
 */
export const linebreaksbr = function (text?: string) {
  text = text || "";
  return text.trim().replaceAll("\n", "<br />");
};

export const shortenText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + "…";
};

/**
 * Formats a float as a string with a specified number of decimal places, but
 * doesn't show the decimal places or divider if they're zero
 */
export const significantDecimalFormat = (value: number, decimalPlaces = 2): string => {
  return value.toFixed(decimalPlaces).replace(/0+$/, "").replace(/\.$/, "");
};

/**
 * Same as significantDecimalFormat but includes a name label for the value with
 * plurality handled appropriately
 */
export const significantDecimalFormatLabeled = (value: number, label: string, decimalPlaces = 2): string => {
  return `${significantDecimalFormat(value, decimalPlaces)} ${pluralize(label, value)}`;
};
